import { z } from 'zod'
import {
  PlanBenefitOverrideRuleStatus,
  PlanBenefitOverrideRuleTemplateKey,
} from '../enums/plan-benefit-override-rule.enum'

// Outside the Zod schema — view-only, not editable.
export interface ViewMetadata {
  status: PlanBenefitOverrideRuleStatus
  createdAt: string
  createdByEmail: string | null
  updatedAt: string
  updatedByEmail: string | null
}

export const HIOS_PLAN_ID_REGEX = /^\d{5}[A-Z]{2}\d{7}$/

// z.coerce.number() turns '' into 0, bypassing nonnegative()/min(). Preprocess to undefined so required_error fires.
const numericField = (inner: z.ZodNumber) =>
  z.preprocess((v) => {
    if (v === '' || v === null || v === undefined) return undefined
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isNaN(n) ? undefined : n
  }, inner)

const baseFormSchema = z.object({
  template: z.nativeEnum(PlanBenefitOverrideRuleTemplateKey, {
    required_error: 'Please select a template',
  }),
  hiosPlanId: z
    .string()
    .min(1, 'HIOS Plan ID is required')
    .regex(
      HIOS_PLAN_ID_REGEX,
      'HIOS Plan ID must match format: 12345AB0000000'
    ),
  year: numericField(
    z
      .number({ required_error: 'Plan year is required' })
      .int()
      .min(2020, 'Year must be 2020 or later')
      .max(2035, 'Year must be 2035 or earlier')
  ),
  benefitName: z.string().min(1, 'Please select a benefit'),
  sourceUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  notes: z
    .string()
    .max(1000, 'Notes must be 1000 characters or fewer')
    .optional(),
})

const partialFieldUpdateParamsSchema = z
  .object({
    appliesToDeductible: z.boolean().optional(),
    coinsurance: numericField(
      z.number().min(-1, 'Use -1 for not applicable')
    ).optional(),
    copayAmount: numericField(
      z.number().min(-1, 'Use -1 for not applicable')
    ).optional(),
    benefitDisplayValue: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .refine(
    (obj) => Object.values(obj).some((v) => v !== undefined && v !== null),
    { message: 'At least one field must be selected' }
  )

const minusOneNum = (label: string) =>
  numericField(
    z
      .number({ required_error: `${label} is required` })
      .min(-1, 'Use -1 for not applicable')
  )

const upsertBenefitParamsSchema = z.object({
  // Auto-populated from benefit.sortOrder via ChangeValueCard — not user-editable.
  // Kept optional here so form validation never blocks on a field the user
  // cannot see or fix; the backend owns the hard constraint.
  sort: numericField(
    z.number().int('Must be a whole number').min(1, 'Must be 1 or greater')
  ).optional(),
  serviceNotCovered: z.boolean(),
  appliesToDeductible: z.boolean(),
  isGrouped: z.boolean(),
  coinsurance: minusOneNum('Coinsurance'),
  copayAmount: minusOneNum('Copay amount'),
  copayDayLimit: minusOneNum('Copay day limit'),
  coinsuranceCopayOrder: minusOneNum('Coins/copay order'),
  memberServicePaidCap: minusOneNum('Member service cap'),
  coverageVisitLimit: numericField(
    z
      .number({ required_error: 'Visit limit is required' })
      .int('Must be a whole number')
      .min(-1, 'Use -1 for not applicable')
  ),
  firstDollarVisits: numericField(
    z
      .number({ required_error: 'First-dollar visits is required' })
      .int('Must be a whole number')
      .min(-1, 'Use -1 for not applicable')
  ),
  copayAfterFirstVisits: minusOneNum('Copay after first visits'),
  clusterId: z.string().nullable().optional(),
  benefitDisplayValue: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
})

export const PlanBenefitOverrideFormSchema = baseFormSchema.and(
  z.discriminatedUnion('template', [
    z.object({
      template: z.literal(
        PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE
      ),
      params: partialFieldUpdateParamsSchema,
    }),
    z.object({
      template: z.literal(PlanBenefitOverrideRuleTemplateKey.UPSERT_RULE),
      params: upsertBenefitParamsSchema,
    }),
  ])
)

export type PlanBenefitOverrideFormValues = z.infer<
  typeof PlanBenefitOverrideFormSchema
>

export const TEMPLATE_PARAM_DEFAULTS: Record<
  PlanBenefitOverrideRuleTemplateKey,
  Record<string, unknown>
> = {
  [PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE]: {},
  [PlanBenefitOverrideRuleTemplateKey.UPSERT_RULE]: {
    serviceNotCovered: false,
    appliesToDeductible: false,
    isGrouped: false,
    coinsurance: -1,
    copayAmount: -1,
    copayDayLimit: -1,
    coinsuranceCopayOrder: -1,
    memberServicePaidCap: -1,
    coverageVisitLimit: -1,
    firstDollarVisits: -1,
    copayAfterFirstVisits: -1,
  },
}

// PARTIAL_FIELD_UPDATE: filter out undefined fields before sending to API.
// UPSERT_RULE: no field name conversion needed — form shape = API shape.
export function formParamsToApiParams(
  template: PlanBenefitOverrideRuleTemplateKey,
  params: Record<string, unknown>
): Record<string, unknown> {
  if (template === PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE) {
    const filtered: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) filtered[k] = v
    }
    return filtered
  }
  return params
}

// No API→form conversion needed (no percent fields in these templates).
export function apiParamsToFormParams(
  _template: PlanBenefitOverrideRuleTemplateKey,
  params: Record<string, unknown>
): Record<string, unknown> {
  return params
}
